import classNames from "classnames";
import { useState } from "react";
import { Button } from "../../../components/buttons/button";
import { EmptyState } from "../../../components/empty";
import {
  FindEventCard,
  FindEventCardLoader,
} from "../../../components/event/findEventCard";
import {
  TrendingCardLoader,
  TrendingEventCard,
} from "../../../components/event/trendingEventCard";
import { Icon } from "../../../components/icon/icon";
import { SearchInput } from "../../../components/inputs/searchInput";
import { BgWrapper } from "../../../components/wrapper/bgWrapper";
import {
  useFetchAvailableCategoriesQuery,
  useFindEventsQuery,
} from "../../../redux/features/event/event.api";
import { FindEventsQuery } from "../../../redux/features/event/event.types";
import { useAppSelector } from "../../../redux/hooks";
import { FindEventsCategoryOptions } from "../../../utils/constants";
import { DateFilter } from "./components/dateFilter";
import { LocationFilter } from "./components/locationFilter";
import { PriceFilter } from "./components/priceFilter";

const findEventLimit = 10;

export const FindEventsPage = ({
  navbarHeight,
  pageScrollProgress,
}: {
  navbarHeight: number;
  pageScrollProgress: number;
}) => {
  const { deviceAddressDetails } = useAppSelector((state) => state.auth);

  const [query, setQuery] = useState<FindEventsQuery>({
    page: 1,
    limit: findEventLimit,
    search: undefined,
    sortOrder: "asc",
    location: undefined,
    category: undefined,
    price: undefined,
    date: undefined,
    startDate: undefined,
    endDate: undefined,
    isTrending: undefined,
    country: deviceAddressDetails?.country
      ? deviceAddressDetails.country === "Nigeria"
        ? "Nigeria"
        : "Canada"
      : undefined,
  });

  const { data: events, isFetching: fetchingEvents } = useFindEventsQuery(
    {
      ...query,
      search: query.search === "" ? undefined : query.search,
    },
    { refetchOnMountOrArgChange: true },
  );

  const { data: trendingEvents, isFetching: fetchingTrendingEvents } =
    useFindEventsQuery(
      {
        page: 1,
        limit: 20,
        isTrending: true,
        country: query.country,
      },
      { refetchOnMountOrArgChange: true },
    );

  const { data: availableCatefories, isFetching: fetchingAvailableCategories } =
    useFetchAvailableCategoriesQuery(
      {},
      {
        refetchOnMountOrArgChange: true,
      },
    );

  return (
    <>
      <div
        className={classNames("sticky z-[10] bg-grey-50 py-3 md:py-6", {
          "shadow-sm shadow-grey-900/20": pageScrollProgress > 0.1,
        })}
        style={{
          top: `${navbarHeight}px`,
        }}
      >
        <div className="max-width-website sticky z-[10] flex w-full items-center gap-2.5 overflow-x-scroll">
          <LocationFilter query={query} setQuery={setQuery} />

          <PriceFilter query={query} setQuery={setQuery} />

          <DateFilter query={query} setQuery={setQuery} />
        </div>
      </div>

      <div className="max-width-website relative flex w-full flex-col gap-6 pb-10 pt-3 md:gap-14 md:py-9">
        {!["", 0, undefined].includes(trendingEvents?.data?.data?.length) && (
          <div className={`relative`}>
            <h1 className="text-24 font-unbounded text-grey-800">
              Trending 🔥
            </h1>

            <div className="overflow-x-scroll py-6">
              <div className="relative grid w-max auto-cols-max grid-flow-col gap-[20px]">
                {fetchingTrendingEvents ? (
                  <>
                    {[1, 2, 3, 4, 5]?.map((idx) => {
                      return <TrendingCardLoader key={`${idx}`} />;
                    })}
                  </>
                ) : undefined}

                {!fetchingTrendingEvents &&
                !["", 0, undefined].includes(
                  trendingEvents?.data?.data?.length,
                ) ? (
                  <>
                    {trendingEvents?.data?.data
                      ?.slice(0, 5)
                      ?.map((event, idx) => {
                        return (
                          <TrendingEventCard
                            event={event}
                            key={`${event._id}-${idx}`}
                          />
                        );
                      })}
                  </>
                ) : undefined}
              </div>
            </div>
          </div>
        )}

        <div className="">
          <div className="flex w-full flex-col justify-between gap-4 sm:flex-row sm:items-center">
            <h1 className="text-24 font-unbounded text-grey-800">
              {query.country ? `Events in ${query.country}` : "All Events"}
            </h1>

            <SearchInput
              placeholder="Search"
              searchFunction={(searchVal) => {
                setQuery({
                  ...query,
                  page: 1,
                  search: searchVal,
                  limit: findEventLimit,
                });
              }}
              inputClassName="!rounded-full !border-none"
              iconClassName="text-grey-600"
              className="!max-w-full !rounded-full !border !border-grey-200 !bg-white sm:!max-w-[300px]"
              debouncedSearch={false}
              customElement={
                <Icon
                  svg="arrow-right"
                  width={24}
                  height={24}
                  className="cursor-pointer rounded-full bg-primary p-[5px] text-white"
                />
              }
            />
          </div>

          <div className="mt-6 flex w-fit max-w-full flex-row gap-[10px] overflow-x-scroll md:mt-8">
            {FindEventsCategoryOptions.filter(
              (c) =>
                c.value === "free" ||
                c.value === undefined ||
                (fetchingAvailableCategories
                  ? true
                  : availableCatefories?.data?.includes(c.value!)),
            ).map((cat, idx) => {
              return (
                <BgWrapper
                  loading={fetchingAvailableCategories}
                  loaderHeight={20}
                  key={`category-${idx}`}
                  className={classNames(
                    "soft-shrink flex items-center gap-2 !px-3 !py-[10px]",
                    "cursor-pointer text-sm font-bold",
                    {
                      "!w-[70px]": fetchingAvailableCategories,
                      "!border-[2px] !border-primary":
                        ((query.category === cat.value &&
                          query.price === undefined) ||
                          (query.price === cat.value &&
                            query.category === undefined)) &&
                        !fetchingAvailableCategories,
                    },
                  )}
                  onClick={() => {
                    if (cat.value === "free") {
                      setQuery({
                        ...query,
                        page: 1,
                        limit: findEventLimit,
                        category: undefined,
                        price: "free",
                        date: undefined,
                        startDate: undefined,
                        endDate: undefined,
                      });
                    } else {
                      setQuery({
                        ...query,
                        page: 1,
                        limit: findEventLimit,
                        category: cat.value,
                        price: undefined,
                        date: undefined,
                        startDate: undefined,
                        endDate: undefined,
                      });
                    }
                  }}
                >
                  <Icon
                    svg={cat.icon}
                    className={`rounded-full p-[5px] text-white ${cat.iconClassName}`}
                    width={24}
                    height={24}
                  />
                  <p>{cat.text}</p>
                </BgWrapper>
              );
            })}
          </div>

          {!fetchingEvents &&
          query.search &&
          (events?.data?.totalCount ?? 0) > 0 ? (
            <div className="mt-4">
              <p className="text-base font-semibold text-grey-800">
                {events?.data?.totalCount}{" "}
                {events?.data?.totalCount === 1 ? "event" : "events"} found for
                '{query.search}'
              </p>
            </div>
          ) : undefined}

          <div className="mt-6 grid grid-cols-1 gap-10 md:mt-14 md:grid-cols-2">
            {(fetchingEvents && query.limit! === findEventLimit) ||
            ["", 0, undefined].includes(
              events?.data?.data?.length,
            ) ? undefined : (
              <>
                {events?.data?.data?.map((event, idx) => {
                  return (
                    <FindEventCard event={event} key={`${event._id}-${idx}`} />
                  );
                })}
              </>
            )}

            {fetchingEvents ? (
              <>
                {[1, 2, 3, 4]?.map((idx) => {
                  return <FindEventCardLoader key={`${idx}`} />;
                })}
              </>
            ) : undefined}
          </div>

          <div
            className={classNames(
              "mt-14 flex w-full items-center justify-center",
              {
                hidden:
                  events?.data?.data?.length === events?.data?.totalCount ||
                  ["", 0, undefined].includes(events?.data?.data?.length) ||
                  fetchingEvents,
              },
            )}
          >
            <Button
              type="button"
              text="Show More Events"
              variation="plain-grey"
              loading={fetchingEvents}
              onClick={() => {
                setQuery({
                  ...query,
                  limit: query.limit! + findEventLimit,
                });
              }}
              className="max-w-fit"
            />
          </div>

          {!fetchingEvents &&
          ["", 0, undefined].includes(events?.data?.data?.length) ? (
            <>
              <EmptyState
                text="No Events Found"
                subtext="There are no events that meet your seach"
                className="w-full !bg-transparent"
                image="/assets/images/search-glass.png"
              />
            </>
          ) : undefined}
        </div>
      </div>
    </>
  );
};
